import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { ChartLayout } from 'components/ChartLayout';
import { colorPrimary, colorSecondary } from 'styles';
import { externalTooltipHandler, generateDatasets } from 'utils';
import {
  formatMbArchiveDayTime,
  formatMbArchiveRangeDate,
} from 'utils/mbArchive';

import {
  ChartWrapper,
  Legend,
  LegendDot,
  LegendItem,
  TabsLegendRow,
} from './styles';

const chartTerms = [
  { type: 'sell', value: 'interbank' },
  { type: 'buy', value: 'interbank' },
];

const MAX_DAY_CHART_POINTS = 36;
const CHART_COMPACT_MAX = 400;
const CHART_TABLET_MAX = 640;
const SCREEN_HORIZONTAL_MIN = 1024;
const MAX_DAY_X_TICKS = 7;
const RANGE_TICKS_MIN = 6;
const RANGE_TICKS_MAX = 10;
const RANGE_LABEL_WIDTH_PX = 88;

const getRangeMaxTicks = chartWidth => {
  if (!chartWidth) {
    return RANGE_TICKS_MIN;
  }

  return Math.min(
    RANGE_TICKS_MAX,
    Math.max(RANGE_TICKS_MIN, Math.floor(chartWidth / RANGE_LABEL_WIDTH_PX))
  );
};

const getRangeXTicksConfig = chartWidth => {
  const maxTicks = getRangeMaxTicks(chartWidth);
  const fitsHorizontal = maxTicks * RANGE_LABEL_WIDTH_PX <= chartWidth * 0.9;

  return {
    maxTicks,
    maxRotation: fitsHorizontal ? 0 : 45,
    minRotation: fitsHorizontal ? 0 : 45,
  };
};

const getDayXTicksConfig = chartWidth => {
  if (chartWidth < CHART_COMPACT_MAX) {
    return { maxTicks: 4, maxRotation: 45, minRotation: 45 };
  }

  if (chartWidth < CHART_TABLET_MAX) {
    return { maxTicks: 5, maxRotation: 0, minRotation: 0 };
  }

  if (chartWidth < SCREEN_HORIZONTAL_MIN) {
    return { maxTicks: 6, maxRotation: 0, minRotation: 0 };
  }

  return { maxTicks: MAX_DAY_X_TICKS, maxRotation: 0, minRotation: 0 };
};

const downsampleItems = (items, maxPoints) => {
  if (items.length <= maxPoints) {
    return items;
  }

  const lastIndex = items.length - 1;
  const step = lastIndex / (maxPoints - 1);

  return Array.from({ length: maxPoints }, (_, index) => {
    const sourceIndex =
      index === maxPoints - 1 ? lastIndex : Math.round(index * step);

    return items[sourceIndex];
  });
};

const buildXTicks = ({ maxTicks, maxRotation, minRotation }) => ({
  autoSkip: true,
  maxTicksLimit: maxTicks,
  maxRotation,
  minRotation,
  labelOffset: 1,
  font: {
    size: 13,
  },
  color: 'black',
});

const buildChartOptions = (xTicksConfig, formatRate) => ({
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    autoPadding: false,
    padding: {
      bottom: xTicksConfig.maxRotation > 0 ? 4 : 0,
    },
  },
  events: ['click', 'mousemove'],
  plugins: {
    crosshair: {
      line: {
        color: '#A8B0AC',
        width: 1,
      },
      zoom: {
        enabled: false,
      },
    },
    legend: {
      display: false,
    },
    tooltip: {
      usePointStyle: true,
      enabled: false,
      external: context => externalTooltipHandler(context, 'interbank'),
      callbacks: {
        label(context) {
          return `${context.dataset.label} ${formatRate(context.parsed.y)}`;
        },
      },
    },
  },
  interaction: {
    mode: 'index',
    intersect: false,
  },
  elements: {
    line: {
      tension: 0.2,
    },
    point: {
      radius: 0,
      hoverBackgroundColor: '#fff',
      hoverBorderWidth: 3,
      hoverRadius: 6,
    },
  },
  scales: {
    x: {
      offset: false,
      grid: {
        display: false,
      },
      ticks: buildXTicks(xTicksConfig),
    },
    y: {
      position: 'right',
      ticks: {
        beginAtZero: false,
        callback: yValue => formatRate(yValue),
        color: 'black',
      },
      grid: {
        borderDash: [5, 5],
        drawBorder: false,
      },
      grace: 0.2,
    },
  },
});

const buildChartData = (items, formatMessage, labels, chartId) => {
  const matrix = {
    interbank: {
      sell: items.map(item => item.ask),
      buy: items.map(item => item.bid),
    },
  };

  const termsWithLocale = chartTerms.map(term => ({
    ...term,
    label: formatMessage({ id: `${term.value}.${term.type}` }),
  }));

  return {
    labels,
    datasets: generateDatasets(termsWithLocale, matrix, 'interbank'),
    chartId,
  };
};

const buildDayChartData = (items, formatMessage) => {
  const sortedItems = downsampleItems(
    [...items].reverse(),
    MAX_DAY_CHART_POINTS
  );
  const labels = sortedItems.map(formatMbArchiveDayTime);

  return buildChartData(
    sortedItems,
    formatMessage,
    labels,
    'mb-archive-interbank-day'
  );
};

const buildRangeChartData = (items, formatMessage) => {
  const labels = items.map(formatMbArchiveRangeDate);

  return buildChartData(
    items,
    formatMessage,
    labels,
    'mb-archive-interbank-range'
  );
};

export const MbArchiveChartLegend = () => (
  <Legend>
    <LegendItem>
      <LegendDot $color={colorPrimary} />
      <FormattedMessage id="interbank.buy" />
    </LegendItem>
    <LegendItem>
      <LegendDot $color={colorSecondary} />
      <FormattedMessage id="interbank.sell" />
    </LegendItem>
  </Legend>
);

export const MbArchiveChartTabsRow = ({ children }) => (
  <TabsLegendRow>{children}</TabsLegendRow>
);

MbArchiveChartTabsRow.propTypes = {
  children: PropTypes.node.isRequired,
};

const MbArchiveChart = () => {
  const { currency } = useParams();
  const { formatMessage, formatNumber } = useIntl();
  const mbArchive = useSelector(state => state.currency?.mbArchive);
  const chartContainerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  const formatRate = useMemo(
    () => value =>
      formatNumber(Number(value), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [formatNumber]
  );

  const items = useMemo(
    () => mbArchive?.data?.[currency?.toUpperCase()] || [],
    [mbArchive, currency]
  );

  useEffect(() => {
    if (!items.length) {
      return undefined;
    }

    const node = chartContainerRef.current;

    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      setChartWidth(node.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);

      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, [items.length]);

  const chartState = useMemo(() => {
    if (!items.length) {
      return null;
    }

    const chartData =
      mbArchive?.mode === 'range'
        ? buildRangeChartData(items, formatMessage)
        : buildDayChartData(items, formatMessage);

    const xTicksConfig =
      mbArchive?.mode === 'range'
        ? getRangeXTicksConfig(chartWidth)
        : getDayXTicksConfig(chartWidth);

    return {
      ...chartData,
      options: buildChartOptions(xTicksConfig, formatRate),
    };
  }, [items, mbArchive?.mode, formatMessage, chartWidth, formatRate]);

  if (!chartState) {
    return null;
  }

  const { labels, datasets, chartId, options } = chartState;

  return (
    <ChartWrapper ref={chartContainerRef}>
      <ChartLayout
        key={chartId}
        id={chartId}
        type="line"
        labels={labels}
        datasets={datasets}
        options={options}
      />
    </ChartWrapper>
  );
};

export default MbArchiveChart;
